<template>
  <div class="invite-open-question-wrapper">
    <DialogWrapper
      close-on-cancel
      class="invite-open-question-dialog"
      saveBtnLabelI18nKey="common.btn.invite"
      :show.sync="show"
      :loading="loading"
      :titleI18nKey="`${i18nPrefix}.title`"
      @save="handleSave"
    >
      <div class="tip-message-container">
        <div class="tip">{{ $t(`${i18nPrefix}.message.inviteTip.0`) }}</div>
        <div class="tip">{{ $t(`${i18nPrefix}.message.inviteTip.1`) }}</div>
        <div class="tip">{{ $t(`${i18nPrefix}.message.knockoutTip`) }}</div>
      </div>
      <div class="question-container">
        <TreeQuestionEditor
          v-model="questionList"
          :group-list="groupList"
          :disabled-question-type="disabledQuestionType"
          :hidden-question-template="hiddenQuestionTemplate"
          :disable-question-group="disableQuestionGroup"
          :job-id="jobId"
          survey-type="invite"
          display-mode="job-editor"
        />
      </div>
    </DialogWrapper>
  </div>
</template>

<script>
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import TreeQuestionEditor from '@/components/openQuestion/treeQuestionEditor.vue'
import { mapActions } from 'vuex'
import { QUESTION_TYPE } from '@/constant/openQuestion'
import _ from 'lodash'

export default {
  components: {
    DialogWrapper,
    TreeQuestionEditor
  },
  props: {
    jobId: {
      required: true
    },
    applicationList: {
      required: true
    },
    isAllApplication: {
      default: false
    },
    searchParams: {
      required: false
    },
    hiddenVideoQuestion: {
      default: true
    },
    hiddenAttachmentQuestion: {
      default: true
    },
    hiddenQuestionTemplate: {
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.invitation.openQuestion',
      show: false,
      loading: false,
      groupListLoader: null,
      groupList: [],
      questionList: [],
      invitation: {}
    }
  },
  computed: {
    hasVideoQuestionPermission () {
      return this.hasAnyPermission('PREMIUM_UPLOAD_VIDEO')
    },
    hasAttachmentQuestionPermission () {
      return this.hasAnyPermission('PREMIUM_UPLOAD_PORTFOLIO')
    },
    disableQuestionGroup () {
      return !this.hasAnyPermission('PERM_JOB_QUESTION_TEMPLATE')
    },
    disabledQuestionType () {
      // 邀请补答支持树形结构，但限制不支持树形的问题类型作为子题
      const disabled = [QUESTION_TYPE.SINGLE_SELECT, QUESTION_TYPE.MULTIPLE_SELECT]
      if (!this.hasVideoQuestionPermission && this.hiddenVideoQuestion) {
        disabled.push(QUESTION_TYPE.VIDEO)
      }
      if (!this.hasAttachmentQuestionPermission && this.hiddenAttachmentQuestion) {
        disabled.push(QUESTION_TYPE.ATTACHMENT)
      }
      return disabled
    }
  },
  created () {

  },
  methods: {
    ...mapActions('application', ['inviteOpenQuestion']),
    ...mapActions('openQuestion', ['getQuestionTemplateGroupList']),
    getOptionRelationPayload (option) {
      const relationPayload = {}
      let hasRelationField = false
      _.forEach(['child_question_id', 'child_question_vid', 'next_question_id'], key => {
        if (!Object.prototype.hasOwnProperty.call(option, key)) {
          return
        }
        hasRelationField = true
        if (option[key] !== null && option[key] !== '') {
          relationPayload[key] = option[key]
        }
      })
      if (Object.prototype.hasOwnProperty.call(option, 'child_question')) {
        hasRelationField = true
        if (option.child_question) {
          if (!Object.prototype.hasOwnProperty.call(relationPayload, 'child_question_id') &&
            option.child_question.id !== null && option.child_question.id !== undefined) {
            relationPayload.child_question_id = option.child_question.id
          }
          if (!Object.prototype.hasOwnProperty.call(relationPayload, 'child_question_vid') &&
            option.child_question._vid !== null && option.child_question._vid !== undefined) {
            relationPayload.child_question_vid = option.child_question._vid
          }
        }
      }
      if (_.isEmpty(relationPayload) && hasRelationField) {
        relationPayload.child_question_id = null
      }
      return relationPayload
    },
    serializeOption (option) {
      return {
        ..._.omit(option, ['child_question_id', 'child_question_vid', 'next_question_id', 'child_question']),
        ...this.getOptionRelationPayload(option)
      }
    },
    serializeQuestion (question) {
      const options = _.map(question.options || [], option => this.serializeOption(option))
      const questionPayload = {
        id: question.id,
        vid: question._vid,
        group_id: question.group_id,
        type: question.type,
        title: question.title,
        duration: question.duration,
        required: [true, 1, '1', 'true'].includes(question.required),
        options,
        option_links: _.map(options, option => ({
          option_id: option.id,
          option_vid: option._vid,
          description: option.description,
          ...this.getOptionRelationPayload(option)
        }))
      }
      if (Object.prototype.hasOwnProperty.call(question, 'depth')) {
        questionPayload.depth = question.depth
      }
      if (Object.prototype.hasOwnProperty.call(question, 'parent_question_id')) {
        questionPayload.parent_question_id = question.parent_question_id
      }
      if (Object.prototype.hasOwnProperty.call(question, 'parent_option_id')) {
        questionPayload.parent_option_id = question.parent_option_id
      }
      if (Object.prototype.hasOwnProperty.call(question, 'is_terminal')) {
        questionPayload.is_terminal = question.is_terminal
      }
      return questionPayload
    },
    hasValidOptionIdentities () {
      return _.every(this.questionList, question => {
        const optionVids = _.map(question.options || [], option => option._vid)
        return _.every(optionVids, optionVid => optionVid !== null && optionVid !== undefined && optionVid !== '') &&
          _.uniq(optionVids).length === optionVids.length
      })
    },
    openDialog () {
      this.show = true
    },
    async loadGroupList () {
      if (this.disableQuestionGroup) {
        return this.groupList
      }
      this.loading = true
      if (!this.groupListLoader) {
        this.groupListLoader = this.getQuestionTemplateGroupList({ active_only: 1 }).then(resp => {
          this.groupList = resp
        })
      }
      await this.groupListLoader
      this.loading = false
      return this.groupList
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      if (this.questionList.length < 1) {
        return
      }
      if (!this.hasValidOptionIdentities()) {
        return
      }
      this.loading = true
      try {
        const resp = await this.inviteOpenQuestion({
          job_id: this.jobId,
          is_all_application: this.isAllApplication,
          search_params: this.searchParams,
          candidate_ids: _.map(this.applicationList, item => item.candidate.id),
          question_list: _.map(this.questionList, question => this.serializeQuestion(question))
        })
        if (resp) {
          if (resp.status === 'success') {
            const message = resp.message
            const successCount = message.success_count
            const failCount = message.fail_count
            this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteSuccessInfo`, { successCount, failCount }))
            this.$emit('addInviteQuestion', this.questionList)
            this.$emit('success', resp)
            this.questionList = []
            this.show = false
          } else {
            this.$message.warning(resp.message)
          }
        }
      } catch {
        // 错误提示由请求拦截器统一处理
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    show () {
      if (this.show) {
        this.loadGroupList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.invite-open-question-dialog {
  .tip-message-container {
    .tip {
      margin-top: 5px;
      word-break: break-word;
    }
  }
}
</style>
